<template>
    <li>
        <div
            class="tree-entry folder-node"
            :class="{active: isActive, 'is-drop-target': dropTargetId === folder.id}"
            :style="{ paddingLeft: indent + 'px' }"
            draggable="true"
            @click="$emit('select', folder.id)"
            @dragstart="onDragStart($event)"
            @dragend="onDragEnd"
            @dragover.prevent="onDragOver($event)"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop($event)"
        >
            <a v-if="hasChildren" class="tree-disclosure" @click.stop="$emit('toggle-expand', folder.id)">
                <i class="fas fa-fw" :class="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </a>
            <span v-else class="tree-disclosure-spacer"></span>
            <i class="fad fa-folder fa-fw"></i>
            <span class="tree-label">{{ folder.title || '(Untitled)' }}</span>
            <span v-if="directFileCount" class="tree-count" :title="`${directFileCount} file${directFileCount === 1 ? '' : 's'} in this folder`">
                {{ directFileCount }}
            </span>
            <b-dropdown
                class="tree-menu"
                append-to-body
                aria-role="menu"
                position="is-bottom-left"
                @click.native.stop
            >
                <template #trigger>
                    <a class="tree-menu-trigger" role="button">
                        <i class="fas fa-ellipsis"></i>
                    </a>
                </template>
                <b-dropdown-item @click="$emit('create-subfolder', folder.id)">
                    <i class="fad fa-fw fa-folder-plus mr-2"></i>New Subfolder
                </b-dropdown-item>
                <b-dropdown-item @click="$emit('rename', folder)">
                    <i class="fad fa-fw fa-pen-to-square mr-2"></i>Rename
                </b-dropdown-item>
                <hr class="dropdown-divider" />
                <b-dropdown-item @click="$emit('delete', folder)">
                    <i class="fad fa-fw fa-trash mr-2"></i>Delete Folder
                </b-dropdown-item>
            </b-dropdown>
        </div>
        <ul v-if="hasChildren && isExpanded" class="folder-tree-children">
            <folder-tree-node
                v-for="child in children"
                :key="child.id"
                :folder="child"
                :children-map="childrenMap"
                :file-counts="fileCounts"
                :expanded-set="expandedSet"
                :selected-id="selectedId"
                :drop-target-id="dropTargetId"
                :depth="depth + 1"
                @toggle-expand="(id) => $emit('toggle-expand', id)"
                @select="(id) => $emit('select', id)"
                @create-subfolder="(id) => $emit('create-subfolder', id)"
                @rename="(f) => $emit('rename', f)"
                @delete="(f) => $emit('delete', f)"
                @dragover-folder="(id) => $emit('dragover-folder', id)"
                @dragleave-folder="(id) => $emit('dragleave-folder', id)"
                @drop-folder="(p) => $emit('drop-folder', p)"
                @dragstart-folder="(p) => $emit('dragstart-folder', p)"
                @dragend-folder="() => $emit('dragend-folder')"
            />
        </ul>
    </li>
</template>

<script>
    export default {
        name: 'FolderTreeNode',
        props: {
            folder: { type: Object, required: true },
            childrenMap: { type: Object, required: true },
            fileCounts: { type: Object, default: () => ({}) },
            expandedSet: { type: Object, required: true },
            selectedId: { type: Number, default: null },
            dropTargetId: { type: Number, default: null },
            depth: { type: Number, default: 0 }
        },
        computed: {
            children() {
                return this.childrenMap[this.folder.id] || [];
            },
            hasChildren() {
                return this.children.length > 0;
            },
            directFileCount() {
                return this.fileCounts[this.folder.id] || 0;
            },
            isExpanded() {
                return !!this.expandedSet[this.folder.id];
            },
            isActive() {
                return this.selectedId === this.folder.id;
            },
            indent() {
                return 12 + this.depth * 16;
            }
        },
        methods: {
            onDragStart(e) {
                e.stopPropagation();
                // a folder drag carries its own id; library items use dataTransfer
                // but folders stay internal so we just emit.
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', `folder:${this.folder.id}`); } catch (_) {}
                this.$emit('dragstart-folder', { folderId: this.folder.id });
            },
            onDragEnd() {
                this.$emit('dragend-folder');
            },
            onDragOver(e) {
                e.dataTransfer.dropEffect = 'move';
                this.$emit('dragover-folder', this.folder.id);
            },
            onDragLeave() {
                this.$emit('dragleave-folder', this.folder.id);
            },
            onDrop() {
                this.$emit('drop-folder', { folderId: this.folder.id });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .folder-tree-children {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .tree-entry {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.35rem 0.5rem 0.35rem 0.5rem;
        margin: 0;
        cursor: pointer;
        color: #4a4a4a;
        font-size: 0.88rem;
        transition: background 120ms;
        user-select: none;

        i {
            color: #3c6691;
            flex: 0 0 auto;
        }

        .tree-label {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &:hover {
            background: #eef4fb;

            .tree-menu-trigger {
                opacity: 1;
            }
        }

        &.active {
            background: #336699;
            color: white;

            i {
                color: white;
            }
        }

        &.is-drop-target {
            background: #d5ecff;
            outline: 2px dashed #3c6691;
            outline-offset: -2px;
        }
    }

    .tree-disclosure {
        width: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #8a99ae;
        font-size: 0.75rem;
        cursor: pointer;

        &:hover {
            color: #336699;
        }

        .active & { color: white; }
    }

    .tree-disclosure-spacer {
        display: inline-block;
        width: 18px;
    }

    .tree-count {
        flex: 0 0 auto;
        font-size: 0.72rem;
        color: #8a99ae;
        background: rgba(0, 0, 0, 0.06);
        padding: 1px 7px;
        border-radius: 999px;
        font-weight: 600;
        line-height: 1.25;

        .active & {
            color: white;
            background: rgba(255, 255, 255, 0.25);
        }
    }

    .tree-menu {
        margin-left: auto;
    }

    .tree-menu-trigger {
        opacity: 0;
        padding: 0 4px;
        color: #8a99ae;
        transition: opacity 120ms, color 120ms;

        &:hover { color: #336699; }

        .active & {
            color: rgba(255, 255, 255, 0.85);
            &:hover { color: white; }
        }
    }

    .tree-entry.active .tree-menu-trigger {
        opacity: 1;
    }
</style>
